import { Connection, PublicKey, SystemProgram, Transaction } from "@solana/web3.js";
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
  createAssociatedTokenAccountIdempotentInstruction,
  createTransferCheckedInstruction,
  getAssociatedTokenAddressSync,
} from "@solana/spl-token";

export const SOLANA_RPC = process.env.REACT_APP_SOLANA_RPC || "https://api.mainnet-beta.solana.com";
export const USDC_MINT = process.env.REACT_APP_USDC_MINT || "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v";
export const PLATFORM_WALLET = process.env.REACT_APP_PLATFORM_WALLET || "";
export const PRESALE_WALLET = process.env.REACT_APP_PRESALE_WALLET || "";
export const BW_MINT = process.env.REACT_APP_BW_MINT || "";
export const USDC_DECIMALS = 6;
export const FEE_BPS = 100; // 1%

export function toBaseUnits(str) {
  const s = String(str).trim();
  if (!/^\d+(\.\d{1,6})?$/.test(s) || Number(s) <= 0) {
    throw new Error("Enter a USDC amount with at most 6 decimals");
  }
  const [whole, frac = ""] = s.split(".");
  return BigInt(whole) * 1000000n + BigInt((frac + "000000").slice(0, 6));
}

export function splitAmount(totalUnits) {
  const fee = (totalUnits * BigInt(FEE_BPS)) / 10000n;
  if (fee === 0n) throw new Error("Amount is too small for the 1% platform fee");
  return { merchantUnits: totalUnits - fee, feeUnits: fee };
}

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export async function getLatestBlockhash() {
  const r = await fetch(`${API_BASE}/solana/blockhash`);
  if (!r.ok) throw new Error("Could not fetch the current blockhash — try again in a moment");
  const d = await r.json();
  return { blockhash: d.blockhash, lastValidBlockHeight: d.lastValidBlockHeight };
}

export async function payUsdc({ provider, payer, merchantWallet, merchantUnits, feeUnits }) {
  const mint = new PublicKey(USDC_MINT);
  const payerKey = new PublicKey(payer);
  const merchant = new PublicKey(merchantWallet);
  const platform = new PublicKey(PLATFORM_WALLET);
  const sourceAta = getAssociatedTokenAddressSync(mint, payerKey, false, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID);
  const merchantAta = getAssociatedTokenAddressSync(mint, merchant, false, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID);
  const platformAta = getAssociatedTokenAddressSync(mint, platform, false, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID);

  const tx = new Transaction().add(
    createAssociatedTokenAccountIdempotentInstruction(payerKey, merchantAta, merchant, mint, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID),
    createAssociatedTokenAccountIdempotentInstruction(payerKey, platformAta, platform, mint, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID),
    createTransferCheckedInstruction(sourceAta, mint, merchantAta, payerKey, merchantUnits, USDC_DECIMALS, [], TOKEN_PROGRAM_ID),
    createTransferCheckedInstruction(sourceAta, mint, platformAta, payerKey, feeUnits, USDC_DECIMALS, [], TOKEN_PROGRAM_ID),
  );

  const { blockhash, lastValidBlockHeight } = await getLatestBlockhash();
  tx.feePayer = payerKey;
  tx.recentBlockhash = blockhash;
  tx.lastValidBlockHeight = lastValidBlockHeight;

  const { signature } = await provider.signAndSendTransaction(tx, { preflightCommitment: "confirmed" });
  return typeof signature === "string" ? signature : signature?.toString();
}

export function toLamports(str) {
  const s = String(str).trim();
  if (!/^\d+(\.\d{1,9})?$/.test(s) || Number(s) <= 0) {
    throw new Error("Enter a SOL amount with at most 9 decimals");
  }
  const [whole, frac = ""] = s.split(".");
  return BigInt(whole) * 1000000000n + BigInt((frac + "000000000").slice(0, 9));
}

export async function payPresale({ provider, payer, currency, amountUnits }) {
  if (!PRESALE_WALLET) throw new Error("Presale wallet not configured");
  const payerKey = new PublicKey(payer);
  const receiver = new PublicKey(PRESALE_WALLET);
  const tx = new Transaction();
  if (currency === "USDC") {
    const mint = new PublicKey(USDC_MINT);
    const sourceAta = getAssociatedTokenAddressSync(mint, payerKey, false, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID);
    const destAta = getAssociatedTokenAddressSync(mint, receiver, false, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID);
    tx.add(
      createAssociatedTokenAccountIdempotentInstruction(payerKey, destAta, receiver, mint, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID),
      createTransferCheckedInstruction(sourceAta, mint, destAta, payerKey, amountUnits, USDC_DECIMALS, [], TOKEN_PROGRAM_ID),
    );
  } else {
    tx.add(SystemProgram.transfer({ fromPubkey: payerKey, toPubkey: receiver, lamports: Number(amountUnits) }));
  }
  const { blockhash, lastValidBlockHeight } = await getLatestBlockhash();
  tx.feePayer = payerKey;
  tx.recentBlockhash = blockhash;
  tx.lastValidBlockHeight = lastValidBlockHeight;
  const { signature } = await provider.signAndSendTransaction(tx, { preflightCommitment: "confirmed" });
  return typeof signature === "string" ? signature : signature?.toString();
}

// Mining tier boost: pays the tier price to the official B&W wallet — SOL for the
// base tiers, or during the $1 launch offer either USDC (exact) or SOL (live rate).
export async function payMining({ provider, payer, receiver, currency = "SOL", lamports, usdcUnits }) {
  if (!receiver) throw new Error("Mining wallet not configured");
  const payerKey = new PublicKey(payer);
  const receiverKey = new PublicKey(receiver);
  const tx = new Transaction();
  if (currency === "USDC") {
    const mint = new PublicKey(USDC_MINT);
    const sourceAta = getAssociatedTokenAddressSync(mint, payerKey, false, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID);
    const destAta = getAssociatedTokenAddressSync(mint, receiverKey, false, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID);
    tx.add(
      createAssociatedTokenAccountIdempotentInstruction(payerKey, destAta, receiverKey, mint, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID),
      createTransferCheckedInstruction(sourceAta, mint, destAta, payerKey, Number(usdcUnits), USDC_DECIMALS, [], TOKEN_PROGRAM_ID),
    );
  } else {
    tx.add(SystemProgram.transfer({ fromPubkey: payerKey, toPubkey: receiverKey, lamports: Number(lamports) }));
  }
  const { blockhash, lastValidBlockHeight } = await getLatestBlockhash();
  tx.feePayer = payerKey;
  tx.recentBlockhash = blockhash;
  tx.lastValidBlockHeight = lastValidBlockHeight;
  const { signature } = await provider.signAndSendTransaction(tx, { preflightCommitment: "confirmed" });
  return typeof signature === "string" ? signature : signature?.toString();
}
